import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  RefreshCw,
  Trash2,
  Check,
  Undo2,
  Mail,
  Phone,
  ChevronDown,
  ChevronUp,
  Download,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GoldButton } from "@/components/primitives";
import {
  adminLogin,
  adminMe,
  listEnquiries,
  setEnquiryHandled,
  deleteEnquiry,
  getToken,
  clearToken,
} from "@/lib/adminApi";

const FIELD =
  "border-fs-border bg-transparent text-fs-ink placeholder:text-fs-muted/50 focus-visible:ring-fs-gold focus-visible:ring-1 focus-visible:border-fs-gold/60 h-11";

function LoginView({ onAuthed }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminLogin(email, password);
      toast.success("Welcome back.");
      onAuthed();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-fs-bg px-6">
      <form
        onSubmit={submit}
        className="w-full max-w-md border border-fs-border bg-fs-card p-8 lg:p-10"
        data-testid="admin-login-form"
      >
        <Link to="/" className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center border border-fs-gold/50 font-serif text-base text-fs-gold">
            FS
          </span>
          <span className="font-serif text-xl tracking-tight text-fs-ink">
            Fortuna <span className="text-fs-gold">Strive</span>
          </span>
        </Link>
        <h1 className="mt-8 font-serif text-3xl text-fs-ink">Admin Sign In</h1>
        <p className="mt-2 text-sm text-fs-muted">Enquiry management dashboard.</p>

        <div className="mt-8 space-y-5">
          <div>
            <Label htmlFor="email" className="mb-2 block text-xs uppercase tracking-[0.14em] text-fs-muted">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              data-testid="admin-email"
              className={FIELD}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <Label htmlFor="password" className="mb-2 block text-xs uppercase tracking-[0.14em] text-fs-muted">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              data-testid="admin-password"
              className={FIELD}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
        </div>

        <div className="mt-8">
          <GoldButton type="submit" variant="solid" testId="admin-login-submit" className="w-full" disabled={loading}>
            {loading ? "Signing in…" : "Sign In"}
          </GoldButton>
        </div>
      </form>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-fs-border bg-fs-card p-6">
      <p className="text-xs uppercase tracking-[0.2em] text-fs-muted">{label}</p>
      <p className="mt-3 font-sans text-4xl font-light tabular-nums text-fs-ink">{value}</p>
    </div>
  );
}

function EnquiryRow({ item, onToggle, onDelete }) {
  const [open, setOpen] = useState(false);
  const date = new Date(item.createdAt).toLocaleString();
  return (
    <>
      <tr
        className="border-b border-fs-border align-top hover:bg-fs-card/60"
        data-testid={`enquiry-row-${item.id}`}
      >
        <td className="px-4 py-4">
          <div className="font-medium text-fs-ink">
            {item.firstName} {item.lastName}
          </div>
          <div className="mt-1 text-xs text-fs-muted">{item.companyName}</div>
        </td>
        <td className="px-4 py-4 text-sm text-fs-muted">
          <div className="flex items-center gap-2">
            <Mail className="h-3.5 w-3.5 text-fs-gold" /> {item.workEmail}
          </div>
          <div className="mt-1 flex items-center gap-2">
            <Phone className="h-3.5 w-3.5 text-fs-gold" /> {item.phone}
          </div>
        </td>
        <td className="px-4 py-4 text-sm text-fs-muted">
          <div>{item.primaryService}</div>
          <div className="mt-1 text-xs text-fs-muted/70">Prefers: {item.preferredContact}</div>
        </td>
        <td className="px-4 py-4">
          <span
            className={
              item.smsConsent
                ? "rounded-full border border-fs-gold/40 px-2.5 py-0.5 text-[11px] text-fs-gold"
                : "rounded-full border border-fs-border px-2.5 py-0.5 text-[11px] text-fs-muted/70"
            }
          >
            SMS {item.smsConsent ? "Yes" : "No"}
          </span>
        </td>
        <td className="px-4 py-4">
          <span
            className={
              item.handled
                ? "rounded-full border border-emerald-500/40 px-2.5 py-0.5 text-[11px] text-emerald-400"
                : "rounded-full border border-fs-gold/50 px-2.5 py-0.5 text-[11px] text-fs-gold"
            }
            data-testid={`enquiry-status-${item.id}`}
          >
            {item.handled ? "Handled" : "New"}
          </span>
        </td>
        <td className="whitespace-nowrap px-4 py-4 text-xs text-fs-muted/70">{date}</td>
        <td className="px-4 py-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setOpen((o) => !o)}
              aria-label="Toggle details"
              data-testid={`enquiry-expand-${item.id}`}
              className="flex h-8 w-8 items-center justify-center border border-fs-border text-fs-muted transition-colors hover:border-fs-gold/50 hover:text-fs-gold"
            >
              {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            <button
              onClick={() => onToggle(item)}
              aria-label="Toggle handled"
              data-testid={`enquiry-toggle-${item.id}`}
              className="flex h-8 w-8 items-center justify-center border border-fs-border text-fs-muted transition-colors hover:border-fs-gold/50 hover:text-fs-gold"
            >
              {item.handled ? <Undo2 className="h-4 w-4" /> : <Check className="h-4 w-4" />}
            </button>
            <button
              onClick={() => onDelete(item)}
              aria-label="Delete enquiry"
              data-testid={`enquiry-delete-${item.id}`}
              className="flex h-8 w-8 items-center justify-center border border-fs-border text-fs-muted transition-colors hover:border-red-500/50 hover:text-red-400"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </td>
      </tr>
      {open && (
        <tr className="border-b border-fs-border bg-fs-bg2">
          <td colSpan={7} className="px-4 py-5">
            <div className="text-xs uppercase tracking-[0.2em] text-fs-gold">Business Challenge</div>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-fs-ink">{item.challenge}</p>
            {item.website && (
              <p className="mt-3 text-sm text-fs-muted">
                Website:{" "}
                <a href={item.website} target="_blank" rel="noopener noreferrer" className="text-fs-gold hover:underline">
                  {item.website}
                </a>
              </p>
            )}
            {item.monthlyRevenue && (
              <p className="mt-1 text-sm text-fs-muted">Monthly revenue: {item.monthlyRevenue}</p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function exportEnquiriesCsv(items) {
  const headers = [
    "First Name", "Last Name", "Work Email", "Phone", "Company", "Website",
    "Primary Service", "Monthly Revenue", "Preferred Contact", "SMS Consent",
    "Status", "Business Challenge", "Submitted At",
  ];
  const esc = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = items.map((i) => [
    i.firstName, i.lastName, i.workEmail, i.phone, i.companyName, i.website,
    i.primaryService, i.monthlyRevenue, i.preferredContact,
    i.smsConsent ? "Yes" : "No",
    i.handled ? "Handled" : "New",
    i.challenge,
    new Date(i.createdAt).toISOString(),
  ]);
  const csv = [headers, ...rows].map((r) => r.map(esc).join(",")).join("\r\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `fortuna-strive-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function Dashboard({ onLogout }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listEnquiries();
      setItems(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else {
        toast.error("Could not load enquiries.");
      }
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const onToggle = async (item) => {
    try {
      await setEnquiryHandled(item.id, !item.handled);
      setItems((list) => list.map((i) => (i.id === item.id ? { ...i, handled: !i.handled } : i)));
    } catch {
      toast.error("Update failed.");
    }
  };

  const onDelete = async (item) => {
    if (!window.confirm(`Delete enquiry from ${item.firstName} ${item.lastName}?`)) return;
    try {
      await deleteEnquiry(item.id);
      setItems((list) => list.filter((i) => i.id !== item.id));
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const newCount = items.filter((i) => !i.handled).length;

  return (
    <div className="min-h-screen bg-fs-bg" data-testid="admin-dashboard">
      <header className="border-b border-fs-border bg-fs-bg2">
        <div className="fs-shell flex h-[70px] items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center border border-fs-gold/50 font-serif text-sm text-fs-gold">
              FS
            </span>
            <span className="font-serif text-lg tracking-tight text-fs-ink">Enquiries</span>
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                if (!items.length) return toast.error("No enquiries to export.");
                exportEnquiriesCsv(items);
                toast.success("CSV exported.");
              }}
              className="flex items-center gap-2 border border-fs-border px-3 py-2 text-xs text-fs-muted transition-colors hover:border-fs-gold/50 hover:text-fs-gold"
              data-testid="admin-export"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
            <button
              onClick={load}
              className="flex items-center gap-2 border border-fs-border px-3 py-2 text-xs text-fs-muted transition-colors hover:border-fs-gold/50 hover:text-fs-gold"
              data-testid="admin-refresh"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button
              onClick={onLogout}
              className="flex items-center gap-2 border border-fs-border px-3 py-2 text-xs text-fs-muted transition-colors hover:border-red-500/50 hover:text-red-400"
              data-testid="admin-logout"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="fs-shell py-10">
        <div className="grid gap-4 sm:grid-cols-3">
          <Stat label="Total Enquiries" value={items.length} />
          <Stat label="New / Unhandled" value={newCount} />
          <Stat label="Handled" value={items.length - newCount} />
        </div>

        <div className="mt-10 border border-fs-border bg-fs-bg">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-left">
              <thead>
                <tr className="border-b border-fs-border text-[11px] uppercase tracking-[0.16em] text-fs-muted/70">
                  <th className="px-4 py-4 font-medium">Contact</th>
                  <th className="px-4 py-4 font-medium">Reach</th>
                  <th className="px-4 py-4 font-medium">Service</th>
                  <th className="px-4 py-4 font-medium">SMS</th>
                  <th className="px-4 py-4 font-medium">Status</th>
                  <th className="px-4 py-4 font-medium">Received</th>
                  <th className="px-4 py-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <EnquiryRow key={item.id} item={item} onToggle={onToggle} onDelete={onDelete} />
                ))}
              </tbody>
            </table>
          </div>
          {!loading && items.length === 0 && (
            <div className="py-16 text-center text-fs-muted" data-testid="admin-empty">
              No enquiries yet.
            </div>
          )}
          {loading && (
            <div className="py-16 text-center text-fs-muted">Loading enquiries…</div>
          )}
        </div>
      </main>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null = checking

  const check = useCallback(async () => {
    if (!getToken()) {
      setAuthed(false);
      return;
    }
    try {
      await adminMe();
      setAuthed(true);
    } catch {
      clearToken();
      setAuthed(false);
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  const logout = () => {
    clearToken();
    setAuthed(false);
  };

  if (authed === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-fs-bg text-fs-muted">
        Loading…
      </div>
    );
  }

  return authed ? (
    <Dashboard onLogout={logout} />
  ) : (
    <LoginView onAuthed={() => setAuthed(true)} />
  );
}
